import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@clerk/clerk-react';
import { X, Package, Truck, ArrowRight, Check, Loader } from 'lucide-react';
import { useCreateFulfillment, useGetRates } from '../../hooks/useFulfillments.js';
import { useWorkspaceStore } from '../../stores/workspace.js';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import type { ShippingRate, Fulfillment } from '@mayksel/types';
import type { ShippingAddressInput, ParcelInput } from '@mayksel/validators';

interface Props {
  orderId: string;
  onClose: () => void;
}

type Step = 'type' | 'manual' | 'address' | 'parcel' | 'rates';

interface AddressState {
  name: string; company: string; street1: string; street2: string;
  city: string; state: string; zip: string; country: string;
  phone: string; email: string;
}

interface ParcelState {
  length: string; width: string; height: string;
  distanceUnit: 'in' | 'cm';
  weight: string; massUnit: 'lb' | 'kg' | 'oz' | 'g';
}

const BLANK_ADDR: AddressState = {
  name: '', company: '', street1: '', street2: '',
  city: '', state: '', zip: '', country: 'US', phone: '', email: '',
};

const BLANK_PARCEL: ParcelState = {
  length: '', width: '', height: '', distanceUnit: 'in', weight: '', massUnit: 'lb',
};

function toShippingAddress(f: AddressState): ShippingAddressInput {
  const addr: ShippingAddressInput = {
    name: f.name, street1: f.street1, city: f.city, state: f.state, zip: f.zip, country: f.country,
  };
  if (f.company) addr.company = f.company;
  if (f.street2) addr.street2 = f.street2;
  if (f.phone) addr.phone = f.phone;
  if (f.email) addr.email = f.email;
  return addr;
}

function toParcel(f: ParcelState): ParcelInput {
  return { length: f.length, width: f.width, height: f.height, distanceUnit: f.distanceUnit, weight: f.weight, massUnit: f.massUnit };
}

export default function FulfillmentModal({ orderId, onClose }: Props) {
  const { t } = useTranslation('fulfillments');
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  const overlayRef = useRef<HTMLDivElement>(null);

  const [step, setStep] = useState<Step>('type');
  const [fulfillmentType, setFulfillmentType] = useState<'manual' | 'shippo' | 'pickup'>('manual');

  // Manual / pickup fields
  const [manualTracking, setManualTracking] = useState('');
  const [manualCarrier, setManualCarrier] = useState('');
  const [manualService, setManualService] = useState('');
  const [notes, setNotes] = useState('');

  // Shippo fields
  const [fromAddress, setFromAddress] = useState<AddressState>({ ...BLANK_ADDR });
  const [toAddress, setToAddress] = useState<AddressState>({ ...BLANK_ADDR });
  const [parcel, setParcel] = useState<ParcelState>({ ...BLANK_PARCEL });
  const [rates, setRates] = useState<ShippingRate[]>([]);
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);
  const [ratesError, setRatesError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);

  const createFulfillment = useCreateFulfillment(orderId);
  const getRates = useGetRates(orderId);

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onClose();
  }

  function handleTypeSelect(type: 'manual' | 'shippo' | 'pickup') {
    setFulfillmentType(type);
    setStep(type === 'shippo' ? 'address' : 'manual');
  }

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    const data = fulfillmentType === 'pickup'
      ? { type: 'pickup' as const, notes: notes || undefined }
      : {
          type: 'manual' as const,
          trackingNumber: manualTracking || undefined,
          carrier: manualCarrier || undefined,
          service: manualService || undefined,
          notes: notes || undefined,
        };

    createFulfillment.mutate(data, {
      onSuccess: () => { toast.success(t('add')); onClose(); },
      onError: () => toast.error(t('errors.create_failed')),
    });
  }

  function handleGetRates(e: React.FormEvent) {
    e.preventDefault();
    setRatesError(null);
    getRates.mutate(
      { fromAddress: toShippingAddress(fromAddress), toAddress: toShippingAddress(toAddress), parcel: toParcel(parcel) },
      {
        onSuccess: (data) => { setRates(data); setStep('rates'); },
        onError: () => setRatesError(t('modal.rates_error')),
      },
    );
  }

  async function handlePurchaseLabel() {
    if (!selectedRateId) return;
    setPurchasing(true);
    try {
      // Step 1: create the fulfillment record
      const token = await getToken();
      const opts = { token: token ?? undefined, workspaceId: workspaceId ?? undefined };

      const fulfillment = await api.post<Fulfillment>(
        `/v1/orders/${orderId}/fulfillments`,
        { type: 'shippo', toAddress: toShippingAddress(toAddress) },
        opts,
      );

      // Step 2: purchase the label using the fresh fulfillment ID
      await api.post<Fulfillment>(
        `/v1/orders/${orderId}/fulfillments/${fulfillment.id}/purchase-label`,
        { rateId: selectedRateId, labelFileType: 'PDF' },
        opts,
      );

      toast.success(t('modal.purchase_label'));
      onClose();
    } catch {
      toast.error(t('modal.purchase_error'));
    } finally {
      setPurchasing(false);
    }
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 'var(--space-4)',
      }}
    >
      <div style={{
        background: 'var(--color-bg)', borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-xl)', width: '100%', maxWidth: 560,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border)',
        }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)' }}>
            {t('modal.create_title')}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 'var(--space-6)' }}>

          {/* Step: type */}
          {step === 'type' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                {t('modal.step_type')}
              </p>
              {(['manual', 'shippo', 'pickup'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => handleTypeSelect(type)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                    padding: 'var(--space-4)', background: 'var(--color-bg-subtle)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
                    cursor: 'pointer', textAlign: 'left', width: '100%',
                  }}
                >
                  {type === 'shippo'
                    ? <Truck size={20} style={{ color: 'var(--color-brand)', flexShrink: 0 }} />
                    : <Package size={20} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />}
                  <span style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                    {t(`type.${type}`)}
                  </span>
                  <ArrowRight size={16} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                </button>
              ))}
            </div>
          )}

          {/* Step: manual / pickup */}
          {step === 'manual' && (
            <form onSubmit={handleManualSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {fulfillmentType === 'manual' && (
                <>
                  <FormField label={t('fields.tracking_number')}>
                    <input value={manualTracking} onChange={(e) => setManualTracking(e.target.value)} placeholder="1Z999AA10123456784" style={inputStyle} />
                  </FormField>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                    <FormField label={t('fields.carrier')}>
                      <input value={manualCarrier} onChange={(e) => setManualCarrier(e.target.value)} placeholder="UPS, FedEx…" style={inputStyle} />
                    </FormField>
                    <FormField label={t('fields.service')}>
                      <input value={manualService} onChange={(e) => setManualService(e.target.value)} placeholder="Ground, Priority…" style={inputStyle} />
                    </FormField>
                  </div>
                </>
              )}
              <FormField label={t('fields.notes')}>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </FormField>
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
                <SecondaryButton onClick={() => setStep('type')}>Back</SecondaryButton>
                <PrimaryButton type="submit" loading={createFulfillment.isPending}>Save</PrimaryButton>
              </div>
            </form>
          )}

          {/* Step: address (Shippo) */}
          {step === 'address' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              <AddressSubForm label={t('fields.from_address')} value={fromAddress} onChange={setFromAddress} />
              <AddressSubForm label={t('fields.to_address')} value={toAddress} onChange={setToAddress} />
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                <SecondaryButton onClick={() => setStep('type')}>Back</SecondaryButton>
                <PrimaryButton onClick={() => setStep('parcel')}>Next</PrimaryButton>
              </div>
            </div>
          )}

          {/* Step: parcel */}
          {step === 'parcel' && (
            <form onSubmit={handleGetRates} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)' }}>
                <FormField label={`${t('fields.length')} *`}>
                  <input value={parcel.length} onChange={(e) => setParcel((p) => ({ ...p, length: e.target.value }))} placeholder="12" required style={inputStyle} />
                </FormField>
                <FormField label={`${t('fields.width')} *`}>
                  <input value={parcel.width} onChange={(e) => setParcel((p) => ({ ...p, width: e.target.value }))} placeholder="8" required style={inputStyle} />
                </FormField>
                <FormField label={`${t('fields.height')} *`}>
                  <input value={parcel.height} onChange={(e) => setParcel((p) => ({ ...p, height: e.target.value }))} placeholder="4" required style={inputStyle} />
                </FormField>
              </div>
              <FormField label={t('fields.distance_unit')}>
                <select value={parcel.distanceUnit} onChange={(e) => setParcel((p) => ({ ...p, distanceUnit: e.target.value as 'in' | 'cm' }))} style={inputStyle}>
                  <option value="in">in</option>
                  <option value="cm">cm</option>
                </select>
              </FormField>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <FormField label={`${t('fields.weight')} *`}>
                  <input value={parcel.weight} onChange={(e) => setParcel((p) => ({ ...p, weight: e.target.value }))} placeholder="1.5" required style={inputStyle} />
                </FormField>
                <FormField label={t('fields.mass_unit')}>
                  <select value={parcel.massUnit} onChange={(e) => setParcel((p) => ({ ...p, massUnit: e.target.value as ParcelState['massUnit'] }))} style={inputStyle}>
                    <option value="lb">lb</option>
                    <option value="oz">oz</option>
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                  </select>
                </FormField>
              </div>
              {ratesError && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-danger)' }}>{ratesError}</p>
              )}
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                <SecondaryButton onClick={() => setStep('address')}>Back</SecondaryButton>
                <PrimaryButton type="submit" loading={getRates.isPending}>
                  {getRates.isPending ? t('modal.getting_rates') : t('modal.get_rates')}
                </PrimaryButton>
              </div>
            </form>
          )}

          {/* Step: rates */}
          {step === 'rates' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {rates.length === 0
                ? <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t('modal.no_rates')}</p>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {rates.map((rate) => (
                      <button
                        key={rate.rateId}
                        onClick={() => setSelectedRateId(rate.rateId)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: 'var(--space-3) var(--space-4)',
                          background: selectedRateId === rate.rateId ? 'var(--color-brand-pale)' : 'var(--color-bg-subtle)',
                          border: `1px solid ${selectedRateId === rate.rateId ? 'var(--color-brand)' : 'var(--color-border)'}`,
                          borderRadius: 'var(--radius-md)', cursor: 'pointer', textAlign: 'left', width: '100%',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                            {rate.carrier} — {rate.serviceName}
                          </div>
                          {rate.estimatedDays !== null && (
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                              {t('modal.estimated_days', { days: rate.estimatedDays })}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)' }}>
                            {rate.currency} {parseFloat(rate.amount).toFixed(2)}
                          </span>
                          {selectedRateId === rate.rateId && <Check size={16} style={{ color: 'var(--color-brand)' }} />}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                <SecondaryButton onClick={() => setStep('parcel')}>Back</SecondaryButton>
                <PrimaryButton onClick={handlePurchaseLabel} loading={purchasing} disabled={!selectedRateId}>
                  {t('modal.purchase_label')}
                </PrimaryButton>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ── Address sub-form ───────────────────────────────────────────────────────

function AddressSubForm({
  label, value, onChange,
}: {
  label: string;
  value: AddressState;
  onChange: (v: AddressState) => void;
}) {
  function set(field: keyof AddressState) {
    return (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [field]: e.target.value });
  }

  return (
    <div>
      <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-3)' }}>
        {label}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <FormField label="Name *"><input value={value.name} onChange={set('name')} required style={inputStyle} /></FormField>
          <FormField label="Company"><input value={value.company} onChange={set('company')} style={inputStyle} /></FormField>
        </div>
        <FormField label="Street *"><input value={value.street1} onChange={set('street1')} required style={inputStyle} /></FormField>
        <FormField label="Apt / Suite"><input value={value.street2} onChange={set('street2')} style={inputStyle} /></FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--space-3)' }}>
          <FormField label="City *"><input value={value.city} onChange={set('city')} required style={inputStyle} /></FormField>
          <FormField label="State *"><input value={value.state} onChange={set('state')} required style={inputStyle} /></FormField>
          <FormField label="ZIP *"><input value={value.zip} onChange={set('zip')} required style={inputStyle} /></FormField>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <FormField label="Country *"><input value={value.country} onChange={set('country')} required maxLength={2} style={inputStyle} /></FormField>
          <FormField label="Phone"><input value={value.phone} onChange={set('phone')} style={inputStyle} /></FormField>
        </div>
      </div>
    </div>
  );
}

// ── Shared UI helpers ──────────────────────────────────────────────────────

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <label style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-secondary)' }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)', color: 'var(--color-text)', background: 'var(--color-bg)',
  width: '100%', boxSizing: 'border-box',
};

function PrimaryButton({
  children, onClick, type = 'button', loading = false, disabled = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  loading?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type={type} onClick={onClick} disabled={loading || disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-4)',
        background: loading || disabled ? 'var(--color-bg-muted)' : 'var(--color-brand)',
        color: loading || disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
        border: 'none', borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)',
        cursor: loading || disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {loading && <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />}
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      style={{
        padding: 'var(--space-2) var(--space-4)', background: 'none',
        color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
